import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import esLocale from '@fullcalendar/core/locales/pt-br';
import withAuth from "../../contexts/AuthContext/withAuth";
import * as api from '../../api/calendario';
import multiMonthPlugin from "@fullcalendar/multimonth";
import jsPDF from "jspdf";
import './calendar.scss';
import { Heading, Select, HStack, Spinner, Center } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

export function Calendar() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState<any[]>([]);
  const [publicacoes, setPublicacoes] = useState<any[]>([]);
  const [mesSelecionado, setMesSelecionado] = useState<string>('');
  const [anoSelecionado, setAnoSelecionado] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const calendarRef = useRef<any>(null);
  const firstDay = 1;

  useEffect(() => {
    const now = new Date();
    const mes = String(now.getMonth() + 1).padStart(2, '0');
    const ano = String(now.getFullYear());
    setMesSelecionado(mes);
    setAnoSelecionado(ano);
  }, []);

  useEffect(() => {
    if (mesSelecionado && anoSelecionado) {
      fetchCalendario();
      const targetDate = `${anoSelecionado}-${mesSelecionado}-01`;
      calendarRef.current?.getApi().gotoDate(targetDate);
    }
  }, [mesSelecionado, anoSelecionado]);

  const fetchCalendario = async () => {
    setLoading(true);
    try {
      const datInicial = `${anoSelecionado}-${mesSelecionado}-01`;
      const diasNoMes = new Date(Number(anoSelecionado), Number(mesSelecionado), 0).getDate();
      const datFinal = `${anoSelecionado}-${mesSelecionado}-${diasNoMes}`;

      const calendarioData = await api.obterCalendario(datInicial, datFinal);
      setPublicacoes(calendarioData.formatoArquivos);

      const eventosMapeados = calendarioData.formatoArquivos.map((item: any) => ({
        title: item.nomePublicacao,
        start: item.datPublicacao,
        backgroundColor: getCorTipoPublicacao(item.codTipoPublicacao),
        borderColor: getCorTipoPublicacao(item.codTipoPublicacao),
        extendedProps: {
          id: item.seqConteudo,
          tipo: item.codTipoPublicacao,
          conteudoRedesDTO: item.conteudoRedesDTO,
          emAprovacao: item.numAprovacao === 1 && item.numAprovado === 0
        }
      }));      
      setEventos(eventosMapeados);
    } catch (error) {
      console.error("Erro ao buscar dados do calendário:", error);
    } finally {
      setLoading(false);
    }
  };

  const gerarPdf = async () => {
    try {
      if (!publicacoes.length) {
        console.error("Nenhuma publicação para exportar.");
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.setFontSize(12);
      const tamanhoPagina = pdf.internal.pageSize.getHeight();
      const marginTop = 15;
      const marginBottom = 15;
      let y = marginTop;
      const maxWidth = 190;

      publicacoes.forEach((pub, index) => {
        const verificarQuebraDeLinha = (linesCount: number, lineHeight = 7) => {
          if (y + (linesCount * lineHeight) > tamanhoPagina - marginBottom) {
            pdf.addPage();
            y = marginTop;
          }
        };

        const nomeTexto = `Nome da Publicação: ${pub.nomePublicacao}`;
        const nomeLinhas = pdf.splitTextToSize(nomeTexto, maxWidth);
        verificarQuebraDeLinha(nomeLinhas.length);
        pdf.text(nomeLinhas, 10, y);
        y += nomeLinhas.length * 7;

        const dataPublicacaoTexto = `Data da Publicação: ${pub.datPublicacao
          .split('-')
          .reverse()
          .join('/')}`;
        const dataPublicacaoLinhas = pdf.splitTextToSize(dataPublicacaoTexto, maxWidth);
        verificarQuebraDeLinha(dataPublicacaoLinhas.length);
        pdf.text(dataPublicacaoLinhas, 10, y);
        y += dataPublicacaoLinhas.length * 7;

        const dataFinal =
          pub.datDespublicar && typeof pub.datDespublicar === 'string'
            ? pub.datDespublicar.split('-').reverse().join('/')
            : 'Não informado';
        const dataFinalTexto = `Data Final da Publicação: ${dataFinal}`;
        const dataFinalLinhas = pdf.splitTextToSize(dataFinalTexto, maxWidth);
        verificarQuebraDeLinha(dataFinalLinhas.length);
        pdf.text(dataFinalLinhas, 10, y);
        y += dataFinalLinhas.length * 7;

        verificarQuebraDeLinha(1);
        y += 5;
      });

      const mesNome = meses.find(mes => mes.value === mesSelecionado)?.label || "mensal";

      pdf.save(`calendario-cms-${mesNome.toLocaleLowerCase()}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar o PDF:', error);
    }
  };

  const getCorTipoPublicacao = (codTipoPublicacao: number) => {
    const cor: { [key: number]: string } = {
      1: "#FFD700",
      2: "#FF6347",
      3: "#1E90FF",
      5: "#9370DB",
      6: "#32CD32"
    }
    return cor[codTipoPublicacao] || "#808080"
  }

  const redirecionamentoEdicao = (info: any) => {
    const { id: seqConteudo, tipo: codTipoPublicacao, conteudoRedesDTO } = info.event.extendedProps
    
    const rotas: Record<number, string> = {
      1: "noticia",
      2: "evento",
      3: "banner",
      5: "imprensa-oficial",
      6: "rede-social"
    }
  
    if (codTipoPublicacao !== 6 && rotas[codTipoPublicacao]) {
      const url = `/cms/publicacao/${rotas[codTipoPublicacao]}/editar-${rotas[codTipoPublicacao]}/${seqConteudo}`
      navigate(url)
    } 
    else if (codTipoPublicacao === 6) {
      if (conteudoRedesDTO && conteudoRedesDTO.codRede != null) {
        const codRede = conteudoRedesDTO.codRede
        const url = `/cms/publicacao/redes-sociais/publicacoes/${codRede}/editar-rede-social/${seqConteudo}`
        navigate(url)
      } else {
        console.error("Redirecionamento não realizado:", conteudoRedesDTO)
      }      
    }
    else {
      console.warn("Redirecionamento não realizado")
    }
  }  
  
  const getConteudos = (conteudo: any) => {
    return (
      <div className="event-content">
        {conteudo.event.extendedProps.emAprovacao && (
          <span className="blinking-dot"></span>
        )}
        {conteudo.event.title}
      </div>
    );
  };

  const meses = [
    { value: "01", label: "Janeiro" },
    { value: "02", label: "Fevereiro" },
    { value: "03", label: "Março" },
    { value: "04", label: "Abril" },
    { value: "05", label: "Maio" },
    { value: "06", label: "Junho" },
    { value: "07", label: "Julho" },
    { value: "08", label: "Agosto" },
    { value: "09", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" }
  ];

  const anos = ["2024", "2025", "2026", "2027"];

  return (
    <div className="App">
      <HStack justifyContent="space-between" alignItems="center" mb="20px">
        <Heading
          mt="30px"
          fontWeight="600"
          fontSize="21px"
          color="#6F6F6F"
        >
          Calendário de publicações
        </Heading>
        <HStack spacing="10px">
          <Select
            className="select-mes"
            value={mesSelecionado}
            onChange={(e) => setMesSelecionado(e.target.value)}
          >
            {meses.map((mes) => (
              <option key={mes.value} value={mes.value}>
                {mes.label}
              </option>
            ))}
          </Select>
          <Select
            value={anoSelecionado}
            onChange={(e) => setAnoSelecionado(e.target.value)}
          >
            {anos.map((ano) => (
              <option key={ano} value={ano}>
                {ano}
              </option>
            ))}
          </Select>
        </HStack>
      </HStack>

      <div className="calendario-wrapper">
        <div className="legenda-calendario">
          {[
            { id: 1, nome: "Notícia", cor: getCorTipoPublicacao(1) },
            { id: 2, nome: "Evento", cor: getCorTipoPublicacao(2) },
            { id: 3, nome: "Banner", cor: getCorTipoPublicacao(3) },
            { id: 6, nome: "Rede Social", cor: getCorTipoPublicacao(6) },
          ].map((item) => (
            <div key={item.id} className="legenda-item">
              <span className="legenda-ponto" style={{ backgroundColor: item.cor }}></span>
              <span>{item.nome}</span>
            </div>
          ))}
        </div>

        <div className="container-calendario">
          {loading && (
            <div className="loading-overlay">
              <Center>
                <Spinner size="xl" color="blue.500" />
              </Center>
            </div>
          )}

          <FullCalendar
            ref={calendarRef}
            firstDay={firstDay}
            locale={esLocale}
            headerToolbar={{
              left: "",
              center: "title",
              right: "pdfButton"
            }}
            customButtons={{
              pdfButton: {
                text: "Versão PDF",
                click: gerarPdf
              }
            }}
            themeSystem="Simplex"
            plugins={[dayGridPlugin, multiMonthPlugin]}
            events={eventos}
            eventDidMount={(info) => {
              info.el.setAttribute("title", info.event.title);
            }}
            eventClick={redirecionamentoEdicao}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            eventContent={getConteudos}
            dayCellClassNames="fc-daygrid-day"
            eventClassNames="custom-event"
          />
        </div>
      </div>
    </div>

  );
}

export default withAuth(Calendar);
